import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";
import type { Session, SessionUser } from "./types";

export const sessionKey = ["auth", "session"] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: () => api<Session>("/api/auth/session/"),
    staleTime: 60_000,
  });
}

export function useLocalLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api<Session>("/api/auth/login/", { method: "POST", json: body }),
    onSuccess: (data) =>
      qc.setQueryData(sessionKey, {
        ...data,
        sso_provider_id: qc.getQueryData<Session>(sessionKey)?.sso_provider_id ?? "",
      }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ end_session_url: string }>("/api/auth/logout/", { method: "POST" }),
    onSuccess: (data) => {
      qc.clear();
      if (data.end_session_url) {
        window.location.assign(data.end_session_url);
      } else {
        window.location.assign("/auth/login");
      }
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Partial<
        Pick<SessionUser, "display_name" | "pseudonym" | "opt_out_leaderboard" | "locale">
      >,
    ) => api<Session>("/api/auth/session/", { method: "PATCH", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}

/** Starts the SSO flow through allauth headless (form POST so the IdP redirect is top-level). */
export function startSsoLogin(providerId: string, next = "/"): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/_allauth/browser/v1/auth/provider/redirect";
  const fields: Record<string, string> = {
    provider: providerId,
    callback_url: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`,
    process: "login",
    csrfmiddlewaretoken: document.cookie.match(/(?:^|; )csrftoken=([^;]+)/)?.[1] ?? "",
  };
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}
